import type { FormHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface FormSectionProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Two-column layout matching TailAdmin's form layout template. The left
 * column describes the section, the right column renders the inputs.
 */
export function FormSection({
  title,
  description,
  children,
  className,
}: FormSectionProps) {
  return (
    <div
      className={cn(
        'grid gap-6 border-b border-gray-200 py-6 first:pt-0 last:border-b-0 last:pb-0 lg:grid-cols-3 dark:border-white/10',
        className,
      )}
    >
      <div className="lg:col-span-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        {description !== undefined && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        )}
      </div>
      <div className="space-y-4 lg:col-span-2">{children}</div>
    </div>
  );
}

export interface FormFieldProps {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/** A single form field with label, inline error and hint text. */
export function FormField({
  label,
  description,
  error,
  htmlFor,
  required,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label !== undefined && (
        <label
          htmlFor={htmlFor}
          className="block text-sm font-medium text-gray-900 dark:text-white"
        >
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {children}
      {description !== undefined && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {description}
        </p>
      )}
      {error && (
        <p className="text-xs font-medium text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export interface FormLayoutProps
  extends Omit<FormHTMLAttributes<HTMLFormElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Top-level wrapper that composes `FormSection`s. Renders a page-level
 * heading plus an optional sticky footer for primary actions.
 */
export function FormLayout({
  title,
  description,
  footer,
  className,
  children,
  ...rest
}: FormLayoutProps) {
  return (
    <form
      className={cn(
        'rounded-2xl border border-gray-200 bg-white shadow-card dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark',
        className,
      )}
      {...rest}
    >
      {(title !== undefined || description !== undefined) && (
        <header className="border-b border-gray-200 px-6 py-5 dark:border-white/10">
          {title !== undefined && (
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {title}
            </h1>
          )}
          {description !== undefined && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </header>
      )}
      <div className="px-6 py-4">{children}</div>
      {footer && (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-6 py-4 dark:border-white/10">
          {footer}
        </footer>
      )}
    </form>
  );
}
